import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/authService';
import type { User, LoginCredentials, RegisterData } from '../types/auth';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // bandera de hidratación
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // hidratación
      _hasHydrated: false,
      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),

      // Actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null });
          const response = await authService.login(credentials);
          set({
            user: response.usuario,
            token: response.token_acceso,
            refreshToken: response.token_refresco,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true, error: null });
          const response = await authService.register(data);
          set({
            user: response.usuario,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: () => {
        // Llamar al servicio de logout PRIMERO para limpiar tokens del servidor
        try {
          authService.logout();
        } catch (error) {
          // Silently handle logout errors
        }
        
        // Limpiar el estado de búsqueda (para que sea solo de sesión)
        try {
          localStorage.removeItem('scout-search-storage');
        } catch (error) {
          // Silently handle localStorage errors
        }
        
        // Limpiar completamente el estado de autenticación
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      refreshAuth: async () => {
        try {
          const { refreshToken } = get();
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          const response = await authService.refreshToken(refreshToken);
          set({
            token: response.token_acceso,
            refreshToken: response.token_refresco || refreshToken, // Keep existing refresh token if not provided
            error: null,
          });
          
          // Fetch updated user data after successful token refresh
          try {
            const userData = await authService.getCurrentUser();
            set({ user: userData, isAuthenticated: true });
          } catch (userError) {
            console.error('Failed to fetch user data after token refresh:', userError);
            // Don't throw here - token refresh was successful
          }
        } catch (error: any) {
          get().logout();
          throw error;
        }
      },

      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) set({ user: { ...user, ...userData } });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Marca la hidratación como finalizada
        state?.setHasHydrated?.(true);
      },
    }
  )
);

// Initialize auth state on app start
let isInitializing = false;

export const initializeAuth = async () => {
  // Prevent multiple simultaneous initializations
  if (isInitializing) {
    console.log('Auth initialization already in progress, skipping...');
    return;
  }

  const { token, user, refreshAuth, logout, setHasHydrated } = useAuthStore.getState();
  
  // Marcar hidratación como completada inmediatamente
  setHasHydrated(true);
  
  // If no token, nothing to initialize
  if (!token) {
    console.log('No token found, skipping auth initialization');
    return;
  }

  // If we already have a valid user object, no need to fetch again
  if (user && user.id) {
    console.log('User already loaded, skipping auth initialization');
    return;
  }

  isInitializing = true;
  
  try {
    // Try to refresh the token to validate it's still valid
    // This also updates the user data if needed
    await refreshAuth();
    console.log('Auth initialization successful - token refreshed');
  } catch (error) {
    console.error('Auth initialization failed - logging out:', error);
    logout();
  } finally {
    isInitializing = false;
  }
};
